import { useEffect, useState } from 'react';
import { titleApi, type Meta, type Review } from '../api';
import { useCurrentTheme, useTitleLayout } from '../theme';

/** Sfeerkleur van de titel, alleen als de skin "Kleur volgt de titel" aan heeft. */
export function useTitleColor(meta: Meta | null): { bg: string; glow: string } | null {
  const on = !!useCurrentTheme()?.components?.followColor;
  const [color, setColor] = useState<{ bg: string; glow: string } | null>(null);
  useEffect(() => {
    setColor(null);
    if (!on || !meta?.tmdbId) return;
    let off = false;
    titleApi.color(meta.type, meta.tmdbId).then((r) => !off && setColor(r.color)).catch(() => undefined);
    return () => { off = true; };
  }, [on, meta?.type, meta?.tmdbId]);
  return on ? color : null;
}

/** Knop om tussen de klassieke en de cinematic titelpagina te wisselen. */
export function LayoutToggle() {
  const [layout, toggle] = useTitleLayout();
  const cine = layout === 'cinematic';
  return (
    <button className="btn btn-icon layout-toggle" onClick={toggle} aria-label={cine ? 'Klassieke weergave' : 'Cinematic weergave'} title={cine ? 'Klassieke weergave' : 'Cinematic weergave'}>
      {cine ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="4" width="7" height="16" rx="1" /><path d="M13 6h8M13 11h8M13 16h5" /></svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M6 14h7M6 10h4" /></svg>
      )}
    </button>
  );
}

/** Recensies van Trakt en TMDB; de eerste drie ingekort, de rest achter "Meer". */
export function Reviews({ meta }: { meta: Meta | null }) {
  const [list, setList] = useState<Review[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    setList(null);
    if (!meta?.tmdbId) return;
    let off = false;
    titleApi.reviews(meta.type, meta.tmdbId).then((r) => !off && setList(r.reviews)).catch(() => !off && setList([]));
    return () => { off = true; };
  }, [meta?.type, meta?.tmdbId]);
  if (!list?.length) return null;
  const shown = all ? list : list.slice(0, 3);
  return (
    <section className="reviews" aria-label="Recensies">
      <h2 className="reviews-title">Recensies</h2>
      <div className="reviews-list" data-focus-group>
        {shown.map((r, i) => (
          <article key={`${r.source}-${i}`} className="review">
            <header className="review-head">
              <strong>{r.author}</strong>
              {r.rating !== null && <span className="review-rating">★ {r.rating}/10</span>}
              <span className={`review-src review-${r.source}`}>{r.source === 'trakt' ? 'Trakt' : 'TMDB'}</span>
              {r.likes > 0 && <span className="subtle">♥ {r.likes}</span>}
            </header>
            <p className={open === i ? 'review-text' : 'review-text clamp-4'} lang="en">{r.text}</p>
            {r.text.length > 320 && (
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(open === i ? null : i)}>{open === i ? 'Minder' : 'Hele recensie'}</button>
            )}
          </article>
        ))}
      </div>
      {list.length > 3 && !all && <button className="btn btn-sm" onClick={() => setAll(true)}>Meer recensies ({list.length - 3})</button>}
    </section>
  );
}
